import React, { useMemo } from "react";
import {
  Typography as MuiTypography,
  Grid,
  Box,
  CircularProgress,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";

import useQueryParams from "../../hooks/useQueryParams";
import useFetchGraphData from "../../hooks/useFetchGraphData";
import { usePostHeight } from "../../hooks/usePostHeight";
import useUsdsBoxplotSeries from "../../hooks/charts/useUsdsBoxplotSeries";
import useUsdsAnnualSeries from "../../hooks/charts/useUsdsAnnualSeries";
import FiltersForm from "../../components/FiltersForm";
import UsdsBoxPlotChartContainer from "../../components/charts/usdsBoxPlot/UsdsBoxPlotChartContainer";
import UsdsLineChartContainer from "../../components/charts/usdsLineChart/UsdsLineChartContainer";
import {
  COMPARISON_PERIODS,
  FLOW_CONDITIONS,
  STATISTICS,
  VIEWS,
  inOptions,
  periodLabel,
  usdsEmbedSchema,
} from "./usdsParams";
import {
  landmarksForReach,
  profileMouths,
} from "../../hooks/charts/useUsdsAnnualSeries";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

/**
 * Embed for the monitoring report. One reach per iframe is the intended use:
 *
 *   /annual-report-visualizations/upstream-to-downstream
 *     ?parameterid=28&reaches=106&year=2025&comparison=5555&view=boxplot
 *
 * `controls=true` shows the filter row, `height` sets the chart height.
 */
export default function UpstreamDownstreamEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(usdsEmbedSchema);

  const { data: parameters = [] } = useFetchGraphData({}, "usds/parameters");
  const { data: reachList = [] } = useFetchGraphData({}, "usds/reaches");
  const { data: landmarks = [] } = useFetchGraphData({}, "usds/landmarks");
  const { data: waterYears = [] } = useFetchGraphData(
    { parameterid: params.parameterid, flow: params.flow },
    "usds/water-years"
  );

  /* Until the water year list arrives there is nothing sensible to draw, so
     the year stays unresolved and the chart hooks wait rather than fetching a
     year that may have no data. */
  const resolvedYear = useMemo(() => {
    const available = waterYears.map((y) => y.water_year);
    /* A year chosen for one parameter may not exist for the next; draw the
       latest year that does rather than an empty chart. While the list is
       still loading the URL's year stands, so the first fetch is not delayed. */
    if (
      Number.isInteger(params.year) &&
      (!available.length || available.includes(params.year))
    )
      return params.year;
    if (available.length) return Math.max(...available);
    return null;
  }, [params.year, waterYears]);

  const boxplot = useUsdsBoxplotSeries(params, resolvedYear);
  const annual = useUsdsAnnualSeries(params, resolvedYear);

  const isLine = params.view === "line";
  const active = isLine ? annual : boxplot;

  const selectedLabel = periodLabel(resolvedYear, resolvedYear, resolvedYear);
  const statisticLabel =
    STATISTICS.find((s) => s.value === params.statistic)?.label ?? "Median";

  const parameterOptions = parameters.map((p) => ({
    label: p.param_descrip,
    value: String(p.parameterid),
  }));
  const yearOptions = waterYears.map((y) => ({
    label: `WY ${y.water_year}`,
    value: String(y.water_year),
  }));
  const reachOptions = reachList.map((r) => ({
    label: r.reach_label,
    value: String(r.reach_groupid),
  }));

  const FILTERS = [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter",
      default: inOptions(params.parameterid, parameterOptions),
      options: parameterOptions,
    },
    {
      type: "select",
      param: "year",
      label: "Water year",
      default: inOptions(resolvedYear, yearOptions),
      options: yearOptions,
    },
    {
      type: "select",
      param: "comparison",
      label: "Compare with",
      default: String(params.comparison),
      options: COMPARISON_PERIODS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
    {
      type: "select",
      param: "flow",
      label: "Flow condition",
      default: params.flow,
      options: FLOW_CONDITIONS.map(({ label, value }) => ({ label, value })),
    },
    {
      type: "select",
      param: "reaches",
      label: "Reaches",
      multi: true,
      default: inOptions(params.reaches, reachOptions),
      options: reachOptions,
    },
    {
      type: "select",
      param: "view",
      label: "View",
      default: params.view,
      options: VIEWS.map(({ label, value }) => ({ label, value })),
    },
  ];

  return (
    <main>
      {params.controls && (
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>
      )}

      {active.isLoading && !active.reaches.length && (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      )}

      {active.isError && (
        <Typography variant="body1" color="error">
          The chart could not be loaded.
        </Typography>
      )}

      <Grid container spacing={6}>
        {active.reaches.map((reach) => (
          <Grid item xs={12} key={reach.reachGroupid}>
            {isLine ? (
              <UsdsLineChartContainer
                reach={reach}
                statisticLabel={statisticLabel}
                scale={params.scale}
                landmarks={landmarksForReach(landmarks, reach.reachGroupid)}
                mouths={profileMouths(
                  landmarksForReach(landmarks, reach.reachGroupid),
                  annual.allReaches
                )}
                height={params.height}
              />
            ) : (
              <UsdsBoxPlotChartContainer
                reach={reach}
                parameter={boxplot.parameter}
                selectedLabel={selectedLabel}
                domain={params.lockYAxis ? boxplot.lockedDomain : null}
                landmarks={landmarksForReach(landmarks, reach.reachGroupid)}
                scale={params.scale}
                height={params.height}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </main>
  );
}
